import { IconButton } from "@material-ui/core";
import { AttachFileOutlined, SaveAlt, VisibilityOutlined, VolumeUpRounded } from "@material-ui/icons";

import documentsService from "../../../domain/services/solicitation/documents.service";
import modalities from "../../../domain/constant/modalities.constant";
import { useState } from "react";
import FirstRequestDownloadModals from "../request-details/firstRequestDownloadModals";
import AnswerAdditionalQuestionsModal from "../request-details/asnwerAdditionalQuestionsModal";
import CustomerSingleUpsellModal from "../request-details/CustomerSingleUpsellModal";
import loginService from "../../../domain/services/login.service";
import customerOfficeService from "../../../domain/services/client/customer.office.service";
import datetimeProvider from "../../../providers/datetime.provider";
import Button from "../../shared/Button";
import Tooltip from "../../shared/Tooltip";
import ExpiredDocumentBadge from '../../shared/ExpiredDocumentBadge';
import { expiredDocumentHint, isExpiredDocument } from '../../../domain/services/solicitation/document.expiration';

const RequestServiceDocumentList = ({
    documents = [],
    openPreview,
    serviceName = '',
    isNotion,
    request,
    variant = 'primary',
    category,
}) => {

    const [openFirstDownloadModals, setOpenFirstDownloadModals] = useState(false);
    const [openQuestionsConfirmModal, setOpenQuestionsConfirmModal] = useState(false);
    const [isSingleUpsellModalOpen, setIsSingleUpsellModalOpen] = useState(false);

    const isPrimary = variant === 'primary';
    const isComplementary = category === 'complementary';

    const getCorrectionLabel = (item) => {
        if (isComplementary) return null;

        const version = item.correctionVersion;
        const isCurrent = item.isCurrent;

        if (isCurrent === true && version > 0)
            return { text: 'Versão corrigida', className: 'text-emerald-700 bg-emerald-50 border border-emerald-100' };

        return null;
    };

    const getItemDocument = (item) => {
        const correctionLabel = getCorrectionLabel(item);

        const showSubtitle = item.isProcessMainDocument || !!isNotion || (isPrimary && serviceName);

        return (
            <div className="text-sm md:text-base flex-1 min-w-0">
                {showSubtitle && (
                    <div className="text-xs font-medium text-gray-500 mb-0.5">
                        {item.isProcessMainDocument ? "Processo" : !!isNotion ? modalities.notion.label : serviceName}
                    </div>
                )}

                <div className="text-gray-900 font-medium truncate">
                    {item.displayName || item.name}
                </div>

                <div className="flex flex-wrap items-center gap-2 mt-1">
                    {correctionLabel && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${correctionLabel.className}`}>
                            {correctionLabel.text}
                        </span>
                    )}
                    {(() => {
                        if (isComplementary) return null;
                        const isPreviousVersion = item.correctionVersion >= 0 && item.isCurrent === false;
                        if (!request?.isInCorrection || isPreviousVersion) return null;
                        return (
                            <span className="bg-orange-50 text-orange-700 border border-orange-100 px-2 py-0.5 rounded text-xs font-medium">
                                Em correção
                            </span>
                        );
                    })()}
                    {(() => {
                        const isPreviousVersion = item.isCurrent === false && item.correctedAt;
                        if (isPreviousVersion && !isComplementary) {
                            return (
                                <span className="text-xs text-gray-500">
                                    Substituída em {datetimeProvider.formatDateTimeBRT(item.correctedAt, 'DD/MM/YYYY')}
                                </span>
                            );
                        }
                        const dateToShow = item.correctedAt || item.created;
                        if (dateToShow && !item.isUnderCorrection) {
                            return (
                                <span className="text-xs text-gray-500">
                                    Atualizado em {datetimeProvider.formatDateTimeBRT(dateToShow, 'DD/MM/YYYY')}
                                </span>
                            );
                        }
                        return null;
                    })()}
                </div>
            </div>
        );
    };

    const getDocumentIcon = (doc) => {
        const isAudio = (doc.fileType || '').includes('audio');
        const Icon = isAudio ? VolumeUpRounded : (doc.icon || AttachFileOutlined);

        return (
            <Icon className={isPrimary ? 'text-gray-700 !h-5 !w-5' : 'text-gray-400 !h-4 !w-4'} />
        );
    };

    const openQuestionsModals = () => {
        const user = loginService.getUserAuthentication();
        const customer = { ...user.customer, name: user.name };

        const incompleteRegister = user.customer.isRegisterIncomplete;
        const officeQuestions = customerOfficeService.getQuestionsToShow(customer.profileQuestions);

        if (!incompleteRegister && !officeQuestions.length) return;

        setOpenQuestionsConfirmModal(true);
    };

    const handleDownload = async (doc) => {
        await documentsService.downloadFile(doc.url, doc.downloadName || doc.name);

        if (!request.isFirstCustomerRequest) return;

        openQuestionsModals();
    };

    const getDocumentsActions = (doc) => {

        if (doc.action)
            return doc.action();

        if (isExpiredDocument(doc))
            return <ExpiredDocumentBadge hint={expiredDocumentHint({ isDeliverable: isPrimary })} />;

        if (!isPrimary) {
            return (
                <div className="flex items-center gap-1">
                    <Tooltip title="Visualizar documento">
                        <IconButton onClick={() => openPreview(doc)} size="small">
                            <VisibilityOutlined className="!text-lg !text-gray-500" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Baixar documento">
                        <IconButton onClick={() => handleDownload(doc)} size="small">
                            <SaveAlt className="!text-lg !text-gray-500" />
                        </IconButton>
                    </Tooltip>
                </div>
            );
        }

        return (
            <div className="flex items-center gap-2">
                <Tooltip title="Visualizar documento">
                    <IconButton color="primary" onClick={() => openPreview(doc)}>
                        <VisibilityOutlined className="!text-xl" />
                    </IconButton>
                </Tooltip>

                <Tooltip title="Clique para baixar seu documento automaticamente">
                    <div>
                        <Button
                            icon={<SaveAlt className="!text-base" />}
                            type="primary"
                            className="!rounded-lg !text-sm md:!text-base h-10 !font-normal"
                            onClick={() => handleDownload(doc)}
                        >
                            Baixar
                        </Button>
                    </div>
                </Tooltip>
            </div>
        );
    };

    if (!documents.length) return null;

    return (
        <>
            {openFirstDownloadModals && (
                <FirstRequestDownloadModals onClose={() => {
                    setOpenFirstDownloadModals(false);
                    setIsSingleUpsellModalOpen(true);
                }} />
            )}

            <CustomerSingleUpsellModal open={isSingleUpsellModalOpen} />

            <AnswerAdditionalQuestionsModal
                open={openQuestionsConfirmModal}
                onCancel={() => {
                    setOpenQuestionsConfirmModal(false);
                    setOpenFirstDownloadModals(true);
                }}
                onOk={() => {
                    setOpenQuestionsConfirmModal(false);
                    setOpenFirstDownloadModals(true);
                }}
            />

            <div className={`flex flex-col ${isPrimary ? 'gap-3' : 'gap-1'}`}>
                {documents.map((doc) => (
                    <div
                        key={doc.id}
                        className={
                            isPrimary
                                ? 'flex flex-col md:flex-row gap-3 md:gap-4 items-stretch md:items-center justify-between rounded-lg border border-main/20 bg-white p-4'
                                : 'flex flex-row gap-3 items-center justify-between px-3 py-2 rounded hover:bg-gray-50 transition-colors'
                        }
                    >
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            {getDocumentIcon(doc)}
                            {getItemDocument(doc)}
                        </div>

                        <div className="flex items-center shrink-0">
                            {getDocumentsActions(doc)}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default RequestServiceDocumentList;
